import { useCallback, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Badge, Button, Card, Skeleton, StatusMessage, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { AuthExpiryBanner } from '../../components';
import { useInstanceAuth } from '../../hooks/instance';
import { CHIP_BASE, CHIP_LABEL, CHIP_TONE, formatRelativeTime } from '../../utils';
import {
  rotateInstanceCredentials,
  revokeInstanceCredentials,
  testInstanceCredentials,
} from '../../../../services';
import { AlertTriangle, Lock } from 'lucide-react';

/**
 * Instance Detail — Authentication tab (CT-4194). Mirrors prototype
 * `instanceAuthHTML`: method overview + identity callout, then connection
 * fields and method-specific actions. Credentials stay write-only (no secret
 * echo); Test / Rotate / Re-authenticate hit the mock endpoints.
 */
const AuthenticationTab = () => {
  const { t } = useTranslation('admin-studio');
  const toast = useToast();
  const { instance } = useOutletContext();
  const { auth, isLoading, error } = useInstanceAuth(instance.providerSlug, instance.instanceId);
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    async (fn, toastKey) => {
      if (busy) return;
      setBusy(true);
      try {
        if (fn) await fn();
        toast.success({ message: t(toastKey), canClose: true });
      } catch {
        toast.error({ message: t('common.requestFailed'), canClose: true });
      } finally {
        setBusy(false);
      }
    },
    [busy, t, toast],
  );

  const onAction = useCallback(
    (actionId) => {
      const toastKey = `integrations.instance.auth.toasts.${actionId}`;
      if (actionId === 'test') {
        run(() => testInstanceCredentials(instance.instanceId), toastKey);
        return;
      }
      if (actionId === 'rotate' || actionId === 'reauthenticate' || actionId === 'upload') {
        run(() => rotateInstanceCredentials(instance.instanceId, {}), toastKey);
        return;
      }
      if (actionId === 'revoke') {
        run(() => revokeInstanceCredentials(instance.instanceId), toastKey);
        return;
      }
      run(null, toastKey);
    },
    [instance.instanceId, run],
  );

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (error || !auth) {
    return <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />;
  }

  const showBanner = instance.status === 'auth_expired' || instance.status === 'auth_expiring';
  const managedTone = auth.managed_by === 'byok' ? CHIP_TONE.purple : CHIP_TONE.accent;
  const connectedAt = formatRelativeTime(auth.connected_at, t) || '—';
  const connectedBy = auth.connected_by?.name ?? '—';
  const color = auth.color ?? '#34d39c';

  return (
    <div className="flex flex-col gap-3.5">
      {showBanner ? (
        <AuthExpiryBanner
          status={instance.status}
          credentialExpiresAt={instance.credentialExpiresAt}
          providerName={instance.providerName}
        />
      ) : null}

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between gap-3.5 border-b border-white/[0.05] bg-white/[0.012] px-[22px] py-[18px]">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <span
              aria-hidden="true"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border"
              style={{
                color,
                background: `${color}1a`,
                borderColor: `${color}50`,
              }}
            >
              <Lock size={18} strokeWidth={1.5} />
            </span>
            <div className="min-w-0">
              <p className="mb-1 text-[15px] font-semibold tracking-[-0.005em] text-content">{auth.label}</p>
              <span
                className="inline-flex items-center rounded-lg border px-2 py-0.5 text-[10.5px] font-bold tracking-[0.02em]"
                style={{ color, borderColor: `${color}50`, background: `${color}10` }}
              >
                {auth.badge}
              </span>
            </div>
          </div>
          <Badge variant="neutral" size="small" className={`${CHIP_BASE} ${managedTone} !uppercase !tracking-[0.04em]`}>
            <span className={CHIP_LABEL}>{t(`integrations.instance.managed.${auth.managed_by}`)}</span>
          </Badge>
        </div>
        <Card.Body className="px-[22px] pb-[18px] pt-4">
          <p className="mb-3.5 text-[13px] leading-[1.6] text-content">{auth.desc}</p>
          <div className="rounded-lg border border-[rgba(251,191,36,0.22)] bg-[rgba(251,191,36,0.05)] px-[13px] py-[11px]">
            <p className="mb-1 text-[10.5px] font-bold uppercase tracking-[0.06em] text-[#fbbf24]">
              {t('integrations.instance.auth.identityHeading')}
            </p>
            <p className="text-xs leading-[1.55] text-content-secondary">{auth.identity_note}</p>
          </div>
        </Card.Body>
      </Card>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-[18px] pb-2.5 pt-3.5">
          <h3 className="text-sm font-semibold tracking-[-0.005em] text-content">
            {t('integrations.instance.auth.detailsTitle')}
          </h3>
          <p className="shrink-0 text-[11px] font-medium text-content-faint">
            {t('integrations.instance.auth.detailsMeta', { method: auth.label.toLowerCase() })}
          </p>
        </div>
        <Card.Body className="px-[18px] pb-0 pt-0">
          <div className="mb-3.5 overflow-hidden rounded-lg border border-white/[0.06]">
            {[
              ...(auth.fields ?? []),
              {
                key: 'connected_by',
                label: t('integrations.instance.auth.connectedBy'),
                value: `${connectedBy} · ${connectedAt}`,
              },
            ].map((field, index, list) => (
              <div
                key={field.key}
                className={[
                  'flex items-center gap-3.5 px-3.5 py-[11px]',
                  index < list.length - 1 ? 'border-b border-white/[0.05]' : '',
                ].join(' ')}
              >
                <span className="w-[38%] shrink-0 text-[11px] font-semibold uppercase tracking-[0.05em] text-content-faint">
                  {field.label}
                </span>
                <span className="min-w-0 flex-1 break-words text-[12.5px] text-content">{field.value}</span>
              </div>
            ))}
          </div>
        </Card.Body>
        <div className="flex flex-wrap items-center gap-2 border-t border-white/[0.04] px-[18px] py-3.5">
          {(auth.actions ?? []).map((action) => (
            <Button
              key={action.id}
              type="button"
              variant={action.variant === 'primary' ? 'classicPrimary' : 'secondary'}
              size="sm"
              disabled={busy}
              onClick={() => onAction(action.id)}
            >
              {t(`integrations.instance.auth.actions.${action.id}`)}
            </Button>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default AuthenticationTab;
